import React, { Component } from 'react';
import T from 'prop-types';
// redux, recompose
import { connect } from 'react-redux';
import { compose } from 'recompose';
import withDataTableController from 'components/DataTable/DataTableController/DataTableController';
import { fetchData, setDataItem, resetResources } from 'reducers/dataTable';
import { fetchUserProfile, updateUserProfile } from 'reducers/profiles';
// components
import { Link } from 'react-router';
import { FormattedMessage } from 'react-intl';
import Button from 'components/Button';
import Panel from 'components/Panel';
import UsersCompanyRolesManageTable from 'components/Admin/Users/UsersCompanyRolesManage/UsersCompanyRolesManageTable';
import UserProfileHeader from 'components/ProfileHeader/UserProfileHeader/UserProfileHeader';
import RolesManageFixedHeader from 'components/Admin/RolesManageFixedHeader/RolesManageFixedHeader';
import StickyFooter from 'components/StickyFooter/StickyFooter';
import TableFilter from 'components/TableFilter';
import PaginationWrapper from 'components/PaginationWrapper';
import MessageBox from 'components/MessageBox';
import SearchBox from 'components/SearchBox';
import Preloader from 'components/Preloader';
// utils
import classnames from 'classnames/bind';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { fetchFromAPI } from 'utils/api';
import { showToastrMessage } from 'utils';
import isEmpty from 'lodash.isempty';
// styles
import styles from '../RolesManage.module.scss';

const cn = classnames.bind(styles);

const headerColumns = [
  { flex: '1 1 120px', name: <FormattedMessage id="general.companyName" />, sortField: 'name' },
  { flex: '1 1 80px', name: <FormattedMessage id="general.role.primary" />, sortField: 'is_owner' },
  { flex: '1 1 80px', name: <FormattedMessage id="general.role.manager" />, sortField: 'is_manager' },
  { flex: '1 1 80px', name: <FormattedMessage id="general.role.caregiver" />, sortField: 'is_caretaker' },
  { flex: '1 1 80px', name: <FormattedMessage id="general.role.veterinarian" />, sortField: 'is_vet' },
  { flex: '1 1 80px', name: <FormattedMessage id="general.role.common" />, sortField: 'is_common' },
];

const tableFilters = [
  { label: <FormattedMessage id="general.allCompanies" />, value: '' },
  { label: <FormattedMessage id="general.role.primary" />, value: 'owner' },
  { label: <FormattedMessage id="general.role.farmManager" />, value: 'manager' },
  { label: <FormattedMessage id="general.role.caregiver" />, value: 'caretaker' },
  { label: <FormattedMessage id="general.role.common" />, value: 'common_user' },
  { label: <FormattedMessage id="general.role.veterinarian" />, value: 'vet' },
];

class UserCompanyRolesManage extends Component {

  state = {
    isUpdatingData: false,
  };

  componentDidMount() {
    const { fetchData, reqParams, fetchUserProfile } = this.props;
    fetchData(reqParams).catch(toastResponseErrors);
    fetchUserProfile().catch(toastResponseErrors);
  }

  componentWillUnmount() {
    this.props.resetResources();
  }

  onFilterChange = (filter) => {
    const { fetchData, reqParams } = this.props;
    fetchData({ ...reqParams, page: 1, role: filter });
  };

  handleRoleChanged = (companyId, name, checked) => {
    const { params: { id } } = this.props;
    const paramsData = { name, company_id: companyId, user_id: id };
    return checked
      ? this.createUserCompanyRole(paramsData)
      : this.removeUserCompanyRole(paramsData);
  };

  createUserCompanyRole = (data) => {
    const { params: { id }, setDataItem, profileUser: { registration_state }, fetchUserProfile } = this.props;
    const reqParams = { method: 'post', body: JSON.stringify(data) };
    this.setState({ isUpdatingData: true });
    return fetchFromAPI(`/admin/users/${id}/company_roles`, reqParams)
      .then(({ resource }) => {
        setDataItem(resource);
        this.setState({ isUpdatingData: false });
        if (registration_state === 'invited') {
          fetchUserProfile().then(() => {
            showToastrMessage('component.toastr.userInvite.successCreated');
          });
        }
      })
      .catch((response) => {
        this.setState({ isUpdatingData: false });
        return toastResponseErrors(response);
      });
  };

  removeUserCompanyRole = (data) => {
    const { params: { id }, setDataItem } = this.props;
    const reqParams = { method: 'delete', body: JSON.stringify(data) };
    this.setState({ isUpdatingData: true });
    return fetchFromAPI(`/admin/users/${id}/company_roles`, reqParams)
      .then(({ resource }) => {
        setDataItem(resource);
        this.setState({ isUpdatingData: false });
      })
      .catch((response) => {
        this.setState({ isUpdatingData: false });
        return toastResponseErrors(response);
      });
  };

  enableUser = () => {
    const { params: { id }, profileUser, updateUserProfile } = this.props;
    const isDisabled = !isEmpty(profileUser) && profileUser.disabled;
    const endpoint = `/admin/users/${id}/${isDisabled ? 'enable' : 'activations'}`;
    const method = isDisabled ? 'put' : 'post';

    updateUserProfile(endpoint, { method })
      .then(() => {
        showToastrMessage('component.toastr.user.activated');
      })
      .catch(toastResponseErrors);
  };

  revokeAllAccess = () => {
    const { params: { id }, fetchData, reqParams } = this.props;
    this.setState({ isUpdatingData: true });
    return fetchFromAPI(`/admin/users/${id}/company_roles/destroy_all`, { method: 'delete' })
      .then(() => {
        this.setState({ isUpdatingData: false });
        showToastrMessage('component.toastr.roles.revoked');
        fetchData(reqParams);
      })
      .catch((response) => {
        this.setState({ isUpdatingData: false });
        return toastResponseErrors(response);
      });
  };

  render() {
    const { isUserLoading, resources, isLoading, meta: { total }, reqParams: { page, per_page, role, search, sort },
      profileUser, params: { id }, authUser, onSearchChange, onSortChange, onPageChange, onPerPageChange } = this.props;
    const { isUpdatingData } = this.state;
    const isUserDisabled = profileUser.status === 'disabled';
    const activeFilter = role && tableFilters.find(({ value }) => value === role).label;

    return (
      <div className={cn('roles-manage')}>
        <UserProfileHeader actualUserId={Number(id)} isSticky isLoading={isUserLoading} user={profileUser}>
          <Button light className="show-for-large mr-10" onClick={this.revokeAllAccess}>
            <i className="fa fa-times-circle mr-5" />
            <FormattedMessage id="general.button.revokeAllRoles" />
          </Button>
          <Link to={`/admin/users/${id}/company-roles`} className="button primary wider show-for-large">
            <FormattedMessage id="general.button.IamDone" />
          </Link>
        </UserProfileHeader>

        {isUserDisabled && (
          <div className="small-12 column">
            <MessageBox type="warning" className="mt-20 mb-0" icon={<i className="fa fa-info-circle" />}>
              <span>
                <FormattedMessage id="component.alertBox.userDisabled" tagName="strong" />
                <FormattedMessage id="component.alertBox.userDisabled.text" />
              </span>
              <button className={cn('message-box-btn')} onClick={this.enableUser}>
                <FormattedMessage id="general.button.enableUser" />
              </button>
            </MessageBox>
          </div>
        )}

        <RolesManageFixedHeader
          className="show-for-large"
          panelTitle={<FormattedMessage id="general.companyRoles" />}
          isEmptyTable={!resources.length}
          search={search}
          onSearchChange={onSearchChange}
          sort={sort}
          onSortChange={onSortChange}
          headerColumns={headerColumns}
        />

        <section className={cn('roles-manage-section')}>
          <div className="small-12 column">
            <Panel>
              <Panel.Heading title={<FormattedMessage id="general.companyRoles" />} className="hide-for-large">
                <SearchBox initialValue={search} onChange={onSearchChange} />
              </Panel.Heading>

              <Panel.Body className={cn('roles-manage-body')}>
                <TableFilter
                  filters={tableFilters}
                  activeFilter={role}
                  onFilterChange={this.onFilterChange}
                  className="hide-for-large"
                >
                  <Button darkGrey onClick={this.revokeAllAccess}>
                    <FormattedMessage id="general.button.revokeAllRoles" />
                  </Button>
                </TableFilter>

                <Preloader isActive={isLoading || isUpdatingData} />

                <PaginationWrapper
                  onlyBottom
                  totalItems={total}
                  currentPage={page}
                  onPageChange={onPageChange}
                  perPage={per_page}
                  onPerPageChange={onPerPageChange}
                >
                  <UsersCompanyRolesManageTable
                    data={resources}
                    handleRoleChanged={this.handleRoleChanged}
                    search={search}
                    filter={activeFilter}
                    user={profileUser}
                    authUser={authUser}
                    isLoading={isLoading || isUpdatingData}
                  />
                </PaginationWrapper>
              </Panel.Body>
            </Panel>
          </div>
        </section>

        <StickyFooter className="hide-for-large">
          <Link to={`/admin/users/${id}/company-roles`} className="button primary big-button">
            <FormattedMessage id="general.button.IamDone" />
          </Link>
        </StickyFooter>
      </div>
    );
  }
}

UserCompanyRolesManage.propTypes = {
  fetchData: T.func.isRequired,
  resetResources: T.func.isRequired,
  setDataItem: T.func.isRequired,
  fetchUserProfile: T.func.isRequired,
  updateUserProfile: T.func.isRequired,
  onSearchChange: T.func.isRequired,
  onPageChange: T.func.isRequired,
  onPerPageChange: T.func.isRequired,
  onSortChange: T.func.isRequired,
  params: T.object,
  authUser: T.object.isRequired,
  profileUser: T.object.isRequired,
  resources: T.array.isRequired,
  meta: T.object.isRequired,
  reqParams: T.object.isRequired,
  isLoading: T.bool.isRequired,
  isUserLoading: T.bool.isRequired,
};

const enhance = compose(
  connect((state) => ({
    authUser: state.auth.user,
    profileUser: state.profiles.user.data,
    isUserLoading: state.profiles.user.isLoading,
    resources: state.dataTable.userCompanyRolesManage.resources,
    reqParams: state.dataTable.userCompanyRolesManage.params,
    meta: state.dataTable.userCompanyRolesManage.meta,
    isLoading: state.dataTable.userCompanyRolesManage.isLoading,
  }), (dispatch, { params: { id } }) => ({
    fetchData: (query) => dispatch(fetchData(`/admin/users/${id}/company_roles`, 'userCompanyRolesManage', query)),
    setDataItem: (data) => dispatch(setDataItem(data, 'userCompanyRolesManage')),
    resetResources: () => dispatch(resetResources('userCompanyRolesManage')),
    updateUserProfile: (path, query) => dispatch(updateUserProfile(path, query)),
    fetchUserProfile: () => dispatch(fetchUserProfile(`/admin/users/${id}`)),
  })),
  withDataTableController('fetchData', 'reqParams'),
);

export default enhance(UserCompanyRolesManage);
